import React from 'react';
import SEOHead from '../components/SEOHead';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import UrgencyBanner from '../components/UrgencyBanner';
import { contactInfo } from '../data/mock';
import { Phone, MessageCircle, Heart, Clock, Shield, Star, CheckCircle, ArrowRight } from 'lucide-react';

const RecupererSonEx = () => {
  return (
    <div className="bg-black min-h-screen">
      <SEOHead 
        title="Récupérer son Ex en 24-72h | Grand Marabout Africain | Retour Affectif Garanti"
        description="Vous voulez récupérer votre ex ? Grand marabout africain spécialiste du retour affectif. Récupérez votre ex en 24 à 72 heures. Magie blanche, noire et rouge. +290 clients satisfaits. Appelez maintenant !"
        keywords="récupérer son ex, récupérer son ex rapidement, comment récupérer son ex, récupérer son ex en 24h, récupérer son ex en 72h, retrouver son ex, reconquérir son ex, retour affectif, marabout, faire revenir son ex"
        canonical="https://retour-affectif-rapide.com/recuperer-son-ex"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[80vh] flex items-center justify-center py-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
          
          <div className="max-w-5xl mx-auto text-center relative z-10">
            <span className="inline-block px-6 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-500 text-sm font-semibold tracking-wide mb-8">
              Spécialiste du Retour Affectif
            </span>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
              <span className="text-amber-500">Récupérer son Ex</span>
              <br />
              <span className="text-3xl sm:text-4xl md:text-5xl">en 24 à 72 Heures</span>
            </h1>
            
            <p className="text-gray-300 text-lg sm:text-xl max-w-3xl mx-auto mb-10 leading-relaxed">
              Vous avez perdu l'amour de votre vie ? <strong className="text-amber-500">Maître Diamma</strong>, grand marabout africain, 
              vous aide à <strong>récupérer votre ex</strong> grâce à des rituels puissants. 
              <strong className="text-amber-500"> Résultats garantis en 24-72h</strong>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a
                href={contactInfo.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-bold text-lg rounded-full transition-all duration-300 hover:scale-105"
              >
                <MessageCircle className="w-6 h-6 mr-3" />
                Consultation WhatsApp
              </a>
              <a
                href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-amber-500 text-amber-500 font-bold text-lg rounded-full hover:bg-amber-500 hover:text-black transition-all duration-300"
              >
                <Phone className="w-6 h-6 mr-3" />
                {contactInfo.phone}
              </a>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-400">
              <span className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-green-500" />+290 clients satisfaits</span>
              <span className="flex items-center"><Clock className="w-4 h-4 mr-2 text-amber-500" />Résultats en 24-72h</span>
              <span className="flex items-center"><Shield className="w-4 h-4 mr-2 text-blue-500" />Confidentialité absolue</span>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-900/50 border border-amber-500/20 rounded-2xl p-8 sm:p-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">
                Comment <span className="text-amber-500">Récupérer son Ex</span> Rapidement ?
              </h2>
              
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p>
                  <strong className="text-white">Récupérer son ex</strong> peut sembler impossible après une rupture douloureuse. 
                  Les disputes, les incompréhensions et la distance ont créé un fossé entre vous. 
                  Mais ne perdez pas espoir : avec les <strong className="text-amber-500">rituels ancestraux africains</strong>, 
                  il est possible de faire revenir l'être aimé.
                </p>
                
                <h3 className="text-2xl font-bold text-white mt-8">Pourquoi les rituels de retour affectif fonctionnent ?</h3>
                <p>
                  Les rituels pour <strong>récupérer son ex</strong> agissent sur les énergies spirituelles qui lient deux personnes. 
                  Même après une séparation, ces liens persistent. Grâce à la <strong>magie blanche, noire ou rouge</strong>, 
                  ces énergies sont réactivées pour raviver les sentiments de votre ex envers vous.
                </p>
                
                <h3 className="text-2xl font-bold text-white mt-8">Les étapes pour récupérer votre ex</h3>
                <ul className="list-disc list-inside space-y-3 ml-4">
                  <li><strong>Consultation gratuite</strong> : Exposez votre situation en toute confidentialité</li>
                  <li><strong>Analyse spirituelle</strong> : Les énergies sont analysées pour déterminer le meilleur rituel</li>
                  <li><strong>Rituel personnalisé</strong> : Un rituel adapté à votre situation est réalisé</li>
                  <li><strong>Résultats rapides</strong> : Votre ex ressent le besoin de vous recontacter sous 24-72h</li>
                </ul>
                
                <h3 className="text-2xl font-bold text-white mt-8">Témoignages de personnes qui ont récupéré leur ex</h3>
                <div className="grid gap-4 mt-4">
                  <div className="bg-black/50 p-4 rounded-xl border border-amber-500/10">
                    <div className="flex items-center mb-2">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
                    </div>
                    <p className="italic">"J'ai récupéré mon ex en seulement 3 jours. Je n'y croyais pas mais ça a marché !"</p>
                    <p className="text-amber-500 mt-2 text-sm">- Marie, Paris</p>
                  </div>
                  <div className="bg-black/50 p-4 rounded-xl border border-amber-500/10">
                    <div className="flex items-center mb-2">
                      {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />)}
                    </div>
                    <p className="italic">"Après 6 mois de séparation, mon ex est revenu. Merci infiniment !"</p>
                    <p className="text-amber-500 mt-2 text-sm">- Thomas, Lyon</p>
                  </div>
                </div>
              </div>
              
              {/* CTA */}
              <div className="mt-12 p-6 bg-amber-500/10 border border-amber-500/30 rounded-xl text-center">
                <p className="text-white font-bold text-xl mb-4">
                  Vous voulez récupérer votre ex ?
                </p>
                <p className="text-gray-300 mb-6">
                  Contactez-nous maintenant pour une consultation confidentielle
                </p>
                <a
                  href={contactInfo.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-amber-500 hover:bg-amber-400 text-black font-bold text-lg rounded-full transition-all duration-300 hover:scale-105"
                >
                  <MessageCircle className="w-5 h-5 mr-3" />
                  Récupérer mon ex maintenant
                  <ArrowRight className="w-5 h-5 ml-3" />
                </a>
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="py-20 px-4 bg-gray-900/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12 text-center">
              Questions sur <span className="text-amber-500">Récupérer son Ex</span>
            </h2>
            
            <div className="space-y-6">
              <div className="bg-black/50 p-6 rounded-xl border border-amber-500/10">
                <h3 className="text-xl font-bold text-white mb-3">Est-il vraiment possible de récupérer son ex ?</h3>
                <p className="text-gray-300">Oui, grâce aux rituels de retour affectif, il est possible de récupérer son ex même après une longue séparation. Les énergies spirituelles peuvent raviver les sentiments et lever les blocages émotionnels.</p>
              </div>
              <div className="bg-black/50 p-6 rounded-xl border border-amber-500/10">
                <h3 className="text-xl font-bold text-white mb-3">Combien de temps faut-il pour récupérer son ex ?</h3>
                <p className="text-gray-300">Les premiers résultats apparaissent généralement entre 24 et 72 heures. Pour les cas plus complexes, comptez jusqu'à 21 jours.</p>
              </div>
              <div className="bg-black/50 p-6 rounded-xl border border-amber-500/10">
                <h3 className="text-xl font-bold text-white mb-3">Mon ex est avec quelqu'un d'autre, puis-je le/la récupérer ?</h3>
                <p className="text-gray-300">Oui, des rituels puissants permettent de rompre les liens avec la nouvelle relation et de raviver les sentiments de votre ex pour vous.</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
      <FloatingWhatsApp />
      <UrgencyBanner />
    </div>
  );
};

export default RecupererSonEx;
